import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { DESKTOP_FOOTER_BREAKPOINT } from '../styles'
import { FC } from 'react'
import { FLOWCODE_MARKETING_BASE_URL } from '@dtx-company/true-common/src/constants/routes'
import { FooterIcon, FooterIcons, FooterLinks, FooterWrapper, MobileFooterWrapper } from './styles'
import { FooterLink } from './FooterLink'
import { FooterTypes, TTextLink } from '@dtx-company/true-common/src/types/footer'
import { GenericButton } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { RootCodeState } from '@dtx-company/inter-app/src/redux/types'
import { Stack } from '@dtx-company/design-system/src'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { TextLink } from '@dtx-company/inter-app/src/utils/links'
import { asText } from '@prismicio/client'
import { footerMock } from '../../../containers/nav/top/v2/__mock__/FooterMock'
import { media } from '@app/common/src/theme'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useIsMounted } from '@dtx-company/true-common/src/hooks/useIsMounted'
import { useMediaQuery } from '@dtx-company/true-common/src/hooks/useMediaQuery'
import FlowcodeLogo from '@dtx-company/shared-components/src/foundation/Icons/NavV3/FlowcodeLogo'
import NextImage from 'next/image'
import useTranslation from 'next-translate/useTranslation'

interface LinkProps {
  title: string
  links: TTextLink[]
}

const COOKIE_PREFERENCES_LINK_ID = 'cookie-preferences'
const PRIVACY_CHOICES_LINK_ID = 'your-privacy-choices'

const LinksSection: FC<LinkProps> = ({ title, links }: LinkProps) => {
  return (
    <Box flexDirection="column">
      <FooterLinks>
        <li>
          <Text color="primary.white" variant="body/small" fontWeight={600} py="4px">
            {title}
          </Text>
        </li>
        {links.map(({ text, link, link_id }, idx) => {
          const linkText = asText(text)
          const linkId = asText(link_id)
          if (linkId === COOKIE_PREFERENCES_LINK_ID) {
            return (
              link &&
              text && (
                <li key={linkText + idx}>
                  <GenericButton
                    onClick={() => window.OneTrust?.ToggleInfoDisplay?.()}
                    border="none"
                  >
                    <Text as="span" color="primary.white">
                      {asText(text)}
                    </Text>
                  </GenericButton>
                </li>
              )
            )
          }

          if (linkId === PRIVACY_CHOICES_LINK_ID) {
            return (
              linkText &&
              link &&
              text && (
                <li key={linkText + idx}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="privacy-choices"
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.75}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Text as="span" color="primary.white">
                        {asText(text)}
                      </Text>
                      <NextImage
                        src="/icons/privacy-choices.svg"
                        alt="California Consumer Privacy Act (CCPA) Opt-Out Icon"
                        width={25}
                        height={18}
                      />
                    </Stack>
                  </a>
                </li>
              )
            )
          }

          return (
            linkText &&
            link &&
            text && (
              <li key={linkText + idx}>
                <TextLink color="primary.white" href={link.url}>
                  {asText(text)}
                </TextLink>
              </li>
            )
          )
        })}
      </FooterLinks>
    </Box>
  )
}

const Trademark = ({ trademark }: { trademark: FooterTypes['trademark'] }): JSX.Element => {
  return (
    <Box
      flexDirection="row"
      justifyContent="flex-start"
      alignItems="center"
      flexWrap="wrap"
      width="100%"
      gridArea="trademark"
      mt={['52px', '52px', '52px', '0px']}
    >
      {trademark.map(({ text }, idx) => (
        <Text
          key={`string-${idx}`}
          color="primary.white"
          paddingRight="8px"
          fontSize={['12px', '14px']}
        >
          {asText(text)}
        </Text>
      ))}
    </Box>
  )
}

const FooterSocialLinks = ({
  socialmedia
}: {
  socialmedia: FooterTypes['socialmedia']
}): JSX.Element => {
  const { t } = useTranslation('footer')
  return (
    <FooterIcons>
      {socialmedia?.map(({ link, logo }, idx) => (
        <li key={`social-${logo.alt}-${idx}`}>
          <FooterIcon
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('Footer.Socials.iconsAriaLabel', { account: logo.alt })}
          >
            <NextImage height={24} width={24} alt={logo.alt ?? ''} src={logo.url ?? ''} />
          </FooterIcon>
        </li>
      ))}
    </FooterIcons>
  )
}

const DesktopFooter = ({ data }: { data: Partial<FooterTypes> }): JSX.Element => {
  return (
    <FooterWrapper data-testid="desktop-footer-wrapper">
      <Box bg="inherit" width="100%" flexDirection="column" gridArea="logo" gridGap={3}>
        <FooterLink href={FLOWCODE_MARKETING_BASE_URL} aria-label="Flowcode">
          <FlowcodeLogo fill={'#fff'} width={260} height={35} />
        </FooterLink>
        {!!data?.socialmedia?.length && <FooterSocialLinks socialmedia={data.socialmedia} />}
      </Box>
      {!!data?.body?.length && (
        <Box
          padding={'0px 0px 0px 24px'}
          justifyContent="space-between"
          gridColumnGap="12px"
          width="100%"
          gridArea="links"
        >
          {data.body.map(({ primary, items }, idx) => {
            const title = asText(primary.title)
            return <LinksSection key={`${title}-links-${idx}`} title={title} links={items} />
          })}
        </Box>
      )}
      {!!data?.trademark?.length && <Trademark trademark={data.trademark} />}
    </FooterWrapper>
  )
}

export const MobileFooter = ({ data }: { data: Partial<FooterTypes> }): JSX.Element => (
  <MobileFooterWrapper data-testid="mobile-footer-wrapper" padding="80px 24px 24px">
    <Stack gap={2}>
      <FooterLink href={FLOWCODE_MARKETING_BASE_URL} aria-label="Flowcode">
        <FlowcodeLogo fill={'#fff'} width={260} height={35} />
      </FooterLink>

      {!!data?.socialmedia?.length && <FooterSocialLinks socialmedia={data.socialmedia} />}
    </Stack>

    {!!data?.mobilelinks?.length && (
      <Box marginTop="52px">
        <FooterLinks role="list" margin={0}>
          {data.mobilelinks.map(({ text, link, link_id }, idx) => {
            const linkId = asText(link_id)
            if (linkId === COOKIE_PREFERENCES_LINK_ID) {
              return (
                <li key={`${text}-${idx}`}>
                  <GenericButton
                    onClick={() => window.OneTrust?.ToggleInfoDisplay?.()}
                    display="inline"
                    border="none"
                  >
                    <Text variant="body/small" as="span" color="primary.white" fontWeight={600}>
                      {text}
                    </Text>
                  </GenericButton>
                </li>
              )
            }

            if (linkId === PRIVACY_CHOICES_LINK_ID) {
              return (
                link &&
                text && (
                  <li key={`${text}-${idx}`}>
                    <FooterLink href={link.url} data-testid="privacy-choices">
                      <Stack direction="row" alignItems="center" spacing={1.75}>
                        <Text variant="body/small" color="white" fontWeight={600} as="span">
                          {text}
                        </Text>
                        <NextImage
                          src="/icons/privacy-choices.svg"
                          alt="California Consumer Privacy Act (CCPA) Opt-Out Icon"
                          width={25}
                          height={24}
                        />
                      </Stack>
                    </FooterLink>
                  </li>
                )
              )
            }

            return (
              <li key={`${text}-${idx}`}>
                <FooterLink href={link.url}>
                  <Text variant="body/small" color="white" fontWeight={600} as="span">
                    {text}
                  </Text>
                </FooterLink>
              </li>
            )
          })}
        </FooterLinks>
      </Box>
    )}
    {!!data?.trademark?.length && <Trademark trademark={data.trademark} />}
  </MobileFooterWrapper>
)

export const Footer: FC = () => {
  const footer = footerMock
  const isDesktop = useMediaQuery(media.custom(DESKTOP_FOOTER_BREAKPOINT))
  const isMounted = useIsMounted()
  const { hideFooter } = useCodeSelector((state: RootCodeState) => {
    return state.settings
  })

  const displayDesktopGenerator = !hideFooter && (!isMounted() || isDesktop)
  const displayMobileGenerator = !hideFooter && (!isMounted() || !isDesktop)

  return (
    <>
      {displayDesktopGenerator && <DesktopFooter data={footer} />}
      {displayMobileGenerator && <MobileFooter data={footer} />}
    </>
  )
}
